<template>
  <div
    class="ndsDropdown"
    data-testid="nds-dropdown"
    @keydown.esc.prevent="closeDropdown"
  >
    <NdsPopover
      ref="ndsPopover"
      :close-on-content-click="closeOnContentClick"
      :disabled="disabled"
      :do-teleport="doTeleport"
      :full-width="fullWidth"
      :modifiers="modifiers"
      :placement="placement"
      @close="handleClose"
      @open="handleOpen"
    >
      <template #trigger>
        <slot name="trigger" />
      </template>
      <template #popover>
        <div
          class="ndsDropdown__popover"
          :class="{ '-fullWidth': fullWidth }"
          data-testid="nds-dropdown-popover"
        >
          <div
            v-if="$slots.dropdownHeader"
            class="ndsDropdown__header"
            @click.stop
          >
            <slot name="dropdownHeader" />
          </div>

          <div
            class="ndsDropdown__content"
            :class="{ '-overflowVisible': overflowVisible }"
            :style="{ maxHeight: maxHeightStyle }"
          >
            <slot name="dropdownContent" />
          </div>
        </div>
      </template>
    </NdsPopover>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue'
import NdsPopover from '@/components/NewDesignSystem/NdsPopover/NdsPopover.vue'
import { NdsPopoverPlacement } from '@/components/NewDesignSystem/NdsPopover/NdsPopover.interfaces'
import type { Modifiers } from 'popper.js'

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

const props = defineProps({
  closeOnContentClick: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  doTeleport: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: Number,
    default: 300,
  },
  overflowVisible: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String as PropType<NdsPopoverPlacement>,
    default: NdsPopoverPlacement.BOTTOM_START,
  },
  modifiers: {
    type: Object as PropType<Modifiers>,
    default: () => {},
  },
})

const ndsPopover = ref<InstanceType<typeof NdsPopover> | null>(null)
const showPopover = computed(() => ndsPopover.value?.show)
const maxHeightStyle = computed(() => `min(${props.maxHeight}px, calc(100dvh - var(--spacing-4)))`)

const closeDropdown = () => {
  ndsPopover.value?.closePopover()
}

const handleOpen = () => {
  emit('open')
}

const handleClose = () => {
  emit('close')
}

defineExpose({ closeDropdown, showPopover })
</script>

<style lang="scss" scoped>
.ndsDropdown {
  display: inline-block;

  &__popover {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    min-width: 160px;
    width: fit-content;
    background-color: white;
    border-radius: var(--spacing-0-5);
    box-shadow: var(--box-shadow-e400);

    &.-fullWidth {
      min-width: 100%;
    }
  }

  &__header {
    width: 100%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: var(--spacing-1);

    width: 100%;
    overflow: hidden;
    overflow-y: auto;

    &.-overflowVisible {
      overflow: visible;
    }
  }
}
</style>
